<script setup lang="ts">
    import { ref, watch, watchEffect, nextTick, onMounted, onBeforeUnmount } from 'vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import type { Issue, IssueStatisticsLink, IssueReportCounts } from '../../interfaces';

    const props = defineProps<{
        issue: Issue | null;
        clickMapLinks: IssueStatisticsLink[];
        overview: IssueReportCounts | null;
        show: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'ready'): void;
        (e: 'update:linkMultipleOccurrences', val: boolean): void;
    }>();

    const divRef = ref<HTMLDivElement | null>(null);
    const showOverlay = ref(true);
    const linkMultipleOccurrences = ref(false);

    watchEffect(() => {
        const div = divRef.value;
        const html = props.issue?.BodyHtml;
        if (!div || !html) {
            if (div) div.innerHTML = '';
            return;
        }
        const editableCss = props.issue?.EditableCss ? `<style>${props.issue.EditableCss.replaceAll('.canvas ', '.canvas-temp ')}</style>` : '';
        div.innerHTML = `${html}${editableCss}`;
        nextTick(() => {
            div.classList.toggle('show-click-map-links', showOverlay.value);
            setupOverlay();
            updateMinHeight();
        });
        emit('ready');
    });

    function updateMinHeight() {
        const table = divRef.value?.querySelector<HTMLTableElement>('table');
        if (!table) return;
        const remaining = window.innerHeight - table.getBoundingClientRect().top;
        table.style.minHeight = `${Math.max(remaining, 0)}px`;
    }

    onMounted(() => {
        window.addEventListener('resize', updateMinHeight);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', updateMinHeight);
    });

    watch(showOverlay, (val) => {
        divRef.value?.classList.toggle('show-click-map-links', val);
    });

    watch(
        () => props.clickMapLinks,
        () => nextTick(setupOverlay)
    );
    watch(
        () => props.show,
        (val) => {
            if (val) {
                nextTick(() => {
                    setupOverlay();
                });
            }
        }
    );

    function setupOverlay() {
        const div = divRef.value;
        if (!div || !props.overview) return;

        div.querySelectorAll('.tooltip-container').forEach((el) => el.remove());
        linkMultipleOccurrences.value = false;

        const totalClickers = props.overview.ClickCount;

        for (const link of props.clickMapLinks) {
            const targets = Array.from(div.querySelectorAll<HTMLAnchorElement>('a[href]')).filter((a) => {
                const href = a.getAttribute('href') ?? '';
                return href === link.Url || href.startsWith(link.Url + '?');
            });
            if (!targets.length) continue;

            const pct = totalClickers > 0 ? Math.round((link.ContactCount / totalClickers) * 100) : 0;
            const clickWord = link.ClickCount === 1 ? 'click' : 'clicks';
            let title = `${pct}% (${link.ClickCount} ${clickWord})`;
            if (targets.length > 1) {
                title += ' *';
                linkMultipleOccurrences.value = true;
            }

            const tooltip = `<div class="tooltip-container"><div class="tooltip top keep" role="tooltip"><div class="tooltip-inner">${title}</div><div class="tooltip-arrow p-relative"></div></div></div>`;

            targets.forEach((item) => {
                item.insertAdjacentHTML('afterbegin', tooltip);
            });
        }

        updateMinHeight();
        emit('update:linkMultipleOccurrences', linkMultipleOccurrences.value);
    }

    function preventLinkClicks(e: MouseEvent) {
        if ((e.target as HTMLElement).closest('a')) e.preventDefault();
    }
</script>

<template>
    <div v-show="show">
        <BsAlert
            :show="linkMultipleOccurrences"
            :type="AlertTypes.Info"
            :dismissible="false"
            class="m-0-b"
            :title="$Resources.AlertLinkMultipleOccurrencesHeading"
            :content="$Resources.AlertLinkMultipleOccurrencesBody" />

        <button
            v-if="clickMapLinks.length"
            type="button"
            class="btn btn-primary btn-sm click-map-toggle p-fixed hidden-print"
            @click="showOverlay = !showOverlay">
            {{ showOverlay ? $Resources.LinkOverlayHideButton : $Resources.LinkOverlayShowButton }}
        </button>

        <div v-if="issue?.BodyHtml" ref="divRef" class="canvas-temp" @click="preventLinkClicks"></div>
    </div>
</template>
